import { ChevronUp, LogOut, Settings } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/context";
import { AccessBadge } from "./ui/AccessBadge";

/** Bottom-left account/sign-out popover — the console's only identity chrome now that there's no top bar. */
export function ConsoleAccountWidget() {
  const { user, access, accessSource, usingMockAccess, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const label = user?.displayName ?? user?.email ?? "Signed in";
  const initial = (user?.displayName ?? user?.email ?? "?").charAt(0).toUpperCase();

  async function handleSignOut() {
    setOpen(false);
    // Navigate away from the guarded /console tree first — otherwise
    // ProtectedRoute reacts to the user clearing (via signOut's
    // onAuthStateChanged callback) while we're still mounted there and
    // redirects to /sign-in before this component's own navigate("/") call
    // gets a chance to win.
    navigate("/");
    await signOut();
  }

  return (
    <div className="console-account-widget" ref={containerRef}>
      {open ? (
        <div className="console-account-menu" role="menu">
          <div className="console-account-menu-head">
            <p className="console-account-menu-name">{label}</p>
            {user?.email && user.displayName ? (
              <p className="console-account-menu-email">{user.email}</p>
            ) : null}
            <div className="console-account-menu-badges">
              <AccessBadge level={access} />
              {usingMockAccess ? (
                <span
                  className="badge badge--mock"
                  title={
                    accessSource === "local"
                      ? "Local admin bypass is active (development only)"
                      : "Mock access data is active (development only)"
                  }
                >
                  {accessSource === "local" ? "Local admin" : "Mock access"}
                </span>
              ) : null}
            </div>
          </div>
          <Link
            to="/console/account"
            className="console-account-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <Settings size={16} strokeWidth={1.6} absoluteStrokeWidth aria-hidden="true" />
            Account
          </Link>
          <button
            type="button"
            className="console-account-menu-item"
            style={{ color: "var(--color-danger)" }}
            role="menuitem"
            onClick={() => void handleSignOut()}
          >
            <LogOut
              size={16}
              strokeWidth={1.6}
              absoluteStrokeWidth
              aria-hidden="true"
              style={{ color: "inherit" }}
            />
            Sign Out
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className="console-account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="console-account-avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="console-account-trigger-label">{label}</span>
        <ChevronUp size={14} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
      </button>
    </div>
  );
}
